// Thank You page — visualizzata dopo un'iscrizione a un corso.
// Non indicizzabile (noindex) e spinge eventi conversione su dataLayer.
import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Mail, Phone, ArrowRight, Calendar, Home as HomeIcon, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { pushLeadToDataLayer } from "@/lib/dataLayer";

export default function IscrizioneConfermata() {
    const location = useLocation();
    const trackedRef = useRef(false);

    // Dati dell'iscrizione passati via navigate(state) dal form
    const lead = useMemo(() => location.state || {}, [location.state]);
    const {
        name = "",
        email = "",
        phone = "",
        courseTitle = "",
        courseSlug = "",
        location_preference = "",
    } = lead;

    // Push eventi GTM/GA4 (generate_lead + conversion) una sola volta al mount
    useEffect(() => {
        if (trackedRef.current) return;
        trackedRef.current = true;

        // generate_lead — GA4 standard event
        pushLeadToDataLayer({
            form_name: "enrollment_form",
            name,
            email,
            phone,
            course: courseTitle,
            value: 0,
        });

        // conversion — evento dedicato per Google Ads (sGTM lo instraderà al tag conversione)
        if (typeof window !== "undefined") {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "conversion",
                conversion_type: "course_enrollment",
                form_name: "enrollment_form",
                currency: "CHF",
                value: 0,
                course_slug: courseSlug,
                course_title: courseTitle,
                sede: location_preference,
                page_url: window.location.href,
                page_title: document.title,
            });
        }
    }, []);

    return (
        <>
            <SEO
                title="Iscrizione confermata"
                description="Grazie per la tua iscrizione al corso 7MED. Ti abbiamo inviato una email di conferma con i prossimi passi."
                path="/iscrizione-confermata"
                noindex={true}
            />

            {/* HERO conferma */}
            <section className="bg-ink text-white" data-testid="thankyou-hero">
                <div className="container-7m py-16 sm:py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-[10px] font-mono uppercase tracking-[0.2em] font-bold">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Iscrizione ricevuta
                        </div>
                        <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]" data-testid="thankyou-h1">
                            Grazie{name ? `, ${name.split(" ")[0]}` : ""}.<br />
                            <span className="text-brand">A presto in aula.</span>
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
                            La tua iscrizione{courseTitle ? ` al corso ${courseTitle}` : ""} è stata registrata correttamente.
                            Ti abbiamo inviato una email di conferma{email ? ` all'indirizzo ` : "."}{email && (
                                <span className="text-white font-semibold">{email}</span>
                            )}{email && "."}
                        </p>
                    </div>
                </div>
            </section>

            {/* Prossimi passi */}
            <section className="py-14 sm:py-18 lg:py-24 bg-white">
                <div className="container-7m max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                        {/* Colonna sinistra: prossimi passi */}
                        <div className="lg:col-span-7">
                            <div className="label-eyebrow">Prossimi passi</div>
                            <h2 className="mt-3 font-heading font-black text-3xl sm:text-4xl tracking-tight leading-tight">
                                Cosa succede adesso
                            </h2>

                            <ol className="mt-8 space-y-6" data-testid="thankyou-steps">
                                <StepItem n="01" title="Controlla la tua email">
                                    Entro pochi minuti riceverai la conferma con il riepilogo dei dati. Se non la trovi, controlla la cartella spam.
                                </StepItem>
                                <StepItem n="02" title="Verifica dei dati">
                                    Il nostro team verifica la disponibilità nella sede scelta e conferma la data del corso entro 24 ore lavorative.
                                </StepItem>
                                <StepItem n="03" title="Convocazione ufficiale">
                                    Riceverai la convocazione con orario, indirizzo esatto, materiale richiesto e istruzioni per il pagamento.
                                </StepItem>
                                <StepItem n="04" title="Giorno del corso">
                                    Ti aspettiamo in aula. Al termine ricevi certificato ufficiale e attestato di partecipazione.
                                </StepItem>
                            </ol>

                            {/* Riepilogo iscrizione se disponibile */}
                            {(courseTitle || location_preference) && (
                                <div className="mt-10 border border-ink/10 p-6 bg-ink-muted/5" data-testid="thankyou-summary">
                                    <div className="label-eyebrow !text-ink/60">Riepilogo</div>
                                    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
                                        {courseTitle && (
                                            <>
                                                <dt className="text-ink/60">Corso</dt>
                                                <dd className="font-semibold">{courseTitle}</dd>
                                            </>
                                        )}
                                        {location_preference && (
                                            <>
                                                <dt className="text-ink/60">Sede preferita</dt>
                                                <dd className="font-semibold">{location_preference}</dd>
                                            </>
                                        )}
                                        {email && (
                                            <>
                                                <dt className="text-ink/60">Email</dt>
                                                <dd className="font-semibold break-all">{email}</dd>
                                            </>
                                        )}
                                    </dl>
                                </div>
                            )}
                        </div>

                        {/* Colonna destra: contatti + CTA */}
                        <div className="lg:col-span-5 space-y-6">
                            <div className="border border-ink/10 p-6 bg-white">
                                <div className="label-eyebrow">Servono modifiche?</div>
                                <p className="mt-3 text-sm text-ink/80 leading-relaxed">
                                    Se devi cambiare data, sede o dati personali, contattaci: la modifica prima della convocazione è sempre gratuita.
                                </p>
                                <div className="mt-5 space-y-3">
                                    <a href="tel:+41912615199" className="flex items-center gap-3 text-sm font-semibold hover:text-brand transition-colors" data-testid="thankyou-cta-phone">
                                        <Phone className="w-4 h-4" />
                                        +41 91 261 51 99
                                    </a>
                                    <a href="mailto:backoffice@7med.ch" className="flex items-center gap-3 text-sm font-semibold hover:text-brand transition-colors" data-testid="thankyou-cta-email">
                                        <Mail className="w-4 h-4" />
                                        backoffice@7med.ch
                                    </a>
                                    <a href="https://wa.me/41912615199" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm font-semibold hover:text-brand transition-colors" data-testid="thankyou-cta-whatsapp">
                                        <MessageCircle className="w-4 h-4" />
                                        Scrivi su WhatsApp
                                    </a>
                                </div>
                            </div>

                            <div className="border border-ink p-6 bg-ink text-white">
                                <div className="label-eyebrow !text-brand">Continua a esplorare</div>
                                <div className="mt-4 space-y-3">
                                    {courseSlug && (
                                        <Link to={`/corsi/${courseSlug}`} className="flex items-center justify-between gap-3 py-3 border-b border-white/15 text-sm font-semibold hover:text-brand transition-colors" data-testid="thankyou-link-course">
                                            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> Rivedi il corso</span>
                                            <ArrowRight className="w-4 h-4" />
                                        </Link>
                                    )}
                                    <Link to="/corsi" className="flex items-center justify-between gap-3 py-3 border-b border-white/15 text-sm font-semibold hover:text-brand transition-colors" data-testid="thankyou-link-corsi">
                                        <span>Tutti i corsi</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                    <Link to="/" className="flex items-center justify-between gap-3 py-3 text-sm font-semibold hover:text-brand transition-colors" data-testid="thankyou-link-home">
                                        <span className="inline-flex items-center gap-2"><HomeIcon className="w-4 h-4" /> Torna alla home</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

function StepItem({ n, title, children }) {
    return (
        <li className="flex gap-5">
            <div className="flex-shrink-0 w-12 h-12 border border-ink flex items-center justify-center font-mono font-bold text-sm">
                {n}
            </div>
            <div className="pt-1">
                <div className="font-heading font-bold text-lg tracking-tight">{title}</div>
                <p className="mt-1 text-sm text-ink/70 leading-relaxed">{children}</p>
            </div>
        </li>
    );
}
